// useDecisionAudit — item 15 (Asana 1216373781992284), Rule & Decision Audit View (spec §10.4:
// "RuleSet version history with diffs, per-EUC decision log, override log").
//
// Two independent data sources, one hook, because the screen shows them side by side:
//   1. Ruleset version diffs — item 10 already made every publish an immutable version row with
//      the FULL effective ruleset stored (not just the sparse overrides a consultant touched).
//      GET /rule-config/versions/:version/diff diffs two of those full blobs server-side
//      (rulesetDiff.js) — this hook just fetches + caches results per version so re-opening an
//      already-viewed version's diff doesn't re-hit the network.
//   2. The decision/override log — a new, append-only framework_decision_log table (separate
//      from disposition_overrides/commit_log, which are both "latest state only" and get
//      overwritten). Serves BOTH readings spec §10.4 asks for from the same endpoint: filter by
//      euc_id for "this EUC's decision history," or by event_type for "the override log."
//
// Honest scope note (surfaced in AuditView.tsx's own header, not hidden here): this log starts
// recording from whenever this feature shipped — it is NOT a retroactive backfill of every
// historical ingest/publish re-score, only of override and commit actions going forward.

import { useCallback, useState } from 'react';
import { apiClient } from '../../../api';
import { useProject } from '../../../contexts/ProjectContext';
import type { RulesetVersionSummary } from './useRuleConfig';

export interface DecisionDiffEntry {
  id: string;
  label: string;
  field: string;
  from: any;
  to: any;
  added?: string[];
  removed?: string[];
}
export interface DecisionDiff {
  factors: DecisionDiffEntry[];
  gates: DecisionDiffEntry[];
  boxes: DecisionDiffEntry[];
  thresholds: DecisionDiffEntry[];
  changed_count: number;
}
export interface VersionDiffResult {
  version: number;
  against: number | null;
  against_label: string;
  diff: DecisionDiff;
}

export type DecisionEventType = 'override' | 'commit';
export interface DecisionActor { id: number | string; name: string | null; email: string | null; role?: string | null; }
export interface DecisionLogEvent {
  id: number | string;
  project_id?: number | null;
  euc_id: string;
  event_type: DecisionEventType;
  payload: any;
  actor: DecisionActor;
  at: string;
}

export interface DecisionAuditState {
  /** The project the log is scoped to (null = no project selected). Exposed so the screen can
   *  re-fetch on a project switch without depending on loadLog itself, whose identity also changes
   *  when the euc/event-type filters change (those apply on Enter/button, not on every keystroke). */
  projectId: number | null;
  versions: RulesetVersionSummary[];
  versionsLoaded: boolean;
  versionsError: string | null;
  loadVersions: () => Promise<void>;

  diffs: Record<number, VersionDiffResult | undefined>;
  diffLoading: number | null;
  diffErrors: Record<number, string | undefined>;
  loadDiff: (version: number, against?: number) => Promise<void>;
  clearDiff: (version: number) => void;

  log: DecisionLogEvent[];
  logLoaded: boolean;
  logLoading: boolean;
  logError: string | null;
  eucFilter: string;
  setEucFilter: (v: string) => void;
  eventTypeFilter: '' | DecisionEventType;
  setEventTypeFilter: (v: '' | DecisionEventType) => void;
  loadLog: () => Promise<void>;
}

export function useDecisionAudit(): DecisionAuditState {
  // QA finding 2026-08-05 (Asana 1216373781992284): the log was fetched with no project_id, so the
  // backend returned every project's overrides/commits — one project's "project B only, delete me"
  // test override showed up on Infrastructure Ops Demos. Same projectId derivation as
  // useAttestation.ts / useFrameworkData.ts.
  const { currentProject } = useProject();
  const projectId = currentProject?.id != null && Number.isFinite(Number(currentProject.id))
    ? Number(currentProject.id)
    : null;

  const [versions, setVersions] = useState<RulesetVersionSummary[]>([]);
  const [versionsLoaded, setVersionsLoaded] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  const [diffs, setDiffs] = useState<Record<number, VersionDiffResult | undefined>>({});
  const [diffLoading, setDiffLoading] = useState<number | null>(null);
  const [diffErrors, setDiffErrors] = useState<Record<number, string | undefined>>({});

  const [log, setLog] = useState<DecisionLogEvent[]>([]);
  const [logLoaded, setLogLoaded] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [eucFilter, setEucFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<'' | DecisionEventType>('');

  const loadVersions = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/framework/rule-config/versions');
      setVersions(res?.data?.items || []);
      setVersionsError(null);
    } catch (e: any) {
      setVersionsError(e?.response?.data?.error || e?.message || 'failed to load ruleset version history');
    } finally {
      setVersionsLoaded(true);
    }
  }, []);

  const loadDiff = useCallback(async (version: number, against?: number) => {
    setDiffLoading(version);
    try {
      const qs = against != null ? `?against=${encodeURIComponent(String(against))}` : '';
      const res = await apiClient.get(`/api/framework/rule-config/versions/${version}/diff${qs}`);
      setDiffs((prev) => ({ ...prev, [version]: res?.data || null }));
      setDiffErrors((prev) => ({ ...prev, [version]: undefined }));
    } catch (e: any) {
      setDiffErrors((prev) => ({ ...prev, [version]: e?.response?.data?.error || e?.message || 'failed to load diff' }));
    } finally {
      setDiffLoading((cur) => (cur === version ? null : cur));
    }
  }, []);

  const clearDiff = useCallback((version: number) => {
    setDiffs((prev) => { const next = { ...prev }; delete next[version]; return next; });
  }, []);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectId != null) params.set('project_id', String(projectId));
      if (eucFilter.trim()) params.set('euc_id', eucFilter.trim());
      if (eventTypeFilter) params.set('event_type', eventTypeFilter);
      const qs = params.toString();
      const res = await apiClient.get(`/api/framework/decision-log${qs ? `?${qs}` : ''}`);
      setLog(res?.data?.items || []);
      setLogError(null);
    } catch (e: any) {
      setLogError(e?.response?.data?.error || e?.message || 'failed to load the decision log');
      setLog([]);
    } finally {
      setLogLoading(false);
      setLogLoaded(true);
    }
  }, [projectId, eucFilter, eventTypeFilter]);

  return {
    projectId,
    versions, versionsLoaded, versionsError, loadVersions,
    diffs, diffLoading, diffErrors, loadDiff, clearDiff,
    log, logLoaded, logLoading, logError, eucFilter, setEucFilter, eventTypeFilter, setEventTypeFilter, loadLog,
  };
}
